import { BroadcastChannel, createLeaderElection } from 'broadcast-channel';
import constate from 'constate';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { BrowserStorageVariant, useBrowserStorage } from 'common';

const BROWSER_NOTIFICATIONS_SETTINGS_STORAGE_KEY =
  'staff.browserNotifications.notificationSettings' as const;

const BROWSER_NOTIFICATIONS_WATERMARK_STORAGE_KEY =
  'staff.browserNotifications.watermarkMs' as const;

const BROWSER_NOTIFICATIONS_CHANGE_EVENT_NAME = 'staff.browserNotifications.changed.v1' as const;
const BROWSER_NOTIFICATIONS_LEADER_CHANNEL_NAME = 'staff.browserNotifications.leader.v1' as const;

export enum OptInStatus {
  OptedIn = 'OptedIn',
  OptedOut = 'OptedOut',
  NotAsked = 'NotAsked',
}

export enum LeaderTabStatus {
  Leader = 'Leader',
  Idle = 'Idle',
  Error = 'Error',
}

function dispatchBrowserNotificationsChangedEvent() {
  try {
    window.dispatchEvent(new Event(BROWSER_NOTIFICATIONS_CHANGE_EVENT_NAME));
  } catch {
    // no-op
  }
}

function useBrowserNotifications() {
  const [leaderTabStatus, setLeaderTabStatus] = useState<LeaderTabStatus>(LeaderTabStatus.Idle);
  const {
    storedValues: notificationSettings,
    setStoredValues: setNotificationSettings,
    refetchStoredValues: refetchNotificationSettings,
  } = useBrowserStorage({
    storageKeyName: BROWSER_NOTIFICATIONS_SETTINGS_STORAGE_KEY,
    variant: BrowserStorageVariant.LocalStorage,
    schema: z.object({ optInStatus: z.nativeEnum(OptInStatus) }),
    initialValue: { optInStatus: OptInStatus.NotAsked },
  });

  const { storedValues: watermarkSettings, setStoredValues: setWatermarkSettings } =
    useBrowserStorage({
      storageKeyName: BROWSER_NOTIFICATIONS_WATERMARK_STORAGE_KEY,
      variant: BrowserStorageVariant.LocalStorage,
      schema: z.object({ watermarkMilliseconds: z.number() }),
      initialValue: { watermarkMilliseconds: new Date().getTime() },
    });

  const memoizedSetWatermarkMilliseconds = useCallback(
    (watermarkMilliseconds: number) => {
      setWatermarkSettings({ watermarkMilliseconds });
    },
    [setWatermarkSettings]
  );

  const isBrowserNotificationSupported = typeof window !== 'undefined' && 'Notification' in window;
  const browserNotificationPermission = isBrowserNotificationSupported
    ? Notification.permission
    : 'default';

  const optInToNotificationsInAppStatus = notificationSettings?.optInStatus ?? OptInStatus.NotAsked;
  const isBrowserNotificationEnabled = browserNotificationPermission === 'granted';
  const canRequestPermission =
    isBrowserNotificationSupported && browserNotificationPermission !== 'denied';

  const canEmitNotifications =
    isBrowserNotificationEnabled && optInToNotificationsInAppStatus === OptInStatus.OptedIn;

  const memoizedSetOptInStatusAndDispatchEvent = useCallback(
    (optInStatus: OptInStatus) => {
      setNotificationSettings({ optInStatus });
      dispatchBrowserNotificationsChangedEvent();
    },
    [setNotificationSettings]
  );

  const memoizedOnStorageEvent = useCallback(
    (event: StorageEvent) => {
      if (event.key !== BROWSER_NOTIFICATIONS_SETTINGS_STORAGE_KEY) {
        return;
      }
      refetchNotificationSettings();
    },
    [refetchNotificationSettings]
  );

  useEffect(() => {
    window.addEventListener(BROWSER_NOTIFICATIONS_CHANGE_EVENT_NAME, refetchNotificationSettings);

    return () => {
      window.removeEventListener(
        BROWSER_NOTIFICATIONS_CHANGE_EVENT_NAME,
        refetchNotificationSettings
      );
    };
  }, [refetchNotificationSettings]);

  useEffect(() => {
    // Cross-tab updates (won't fire in the same tab that writes).
    window.addEventListener('storage', memoizedOnStorageEvent);
    return () => {
      window.removeEventListener('storage', memoizedOnStorageEvent);
    };
  }, [memoizedOnStorageEvent]);

  /**
   * This sets up cross-tab leader election so only one tab is the "leader".
   * The leader tab is responsible for browser notification work that should not run in every tab.
   *
   * `useEffect` review
   * Guidelines: https://notable-health.atlassian.net/wiki/spaces/notableengineering/pages/2566389961/React+State+Management#Required-Review
   *
   * This has been reviewed by a UI tech lead to be safe.
   * It initializes the BroadcastChannel and leader election once on mount and cleans them up on unmount.
   */
  useEffect(() => {
    const leaderChannel = new BroadcastChannel(BROWSER_NOTIFICATIONS_LEADER_CHANNEL_NAME);
    const leaderElection = createLeaderElection(leaderChannel);
    let isEffectCurrent = true;

    leaderElection
      .awaitLeadership()
      .then(() => {
        if (isEffectCurrent) {
          setLeaderTabStatus(LeaderTabStatus.Leader);
        }
      })
      .catch((error) => {
        if (isEffectCurrent) {
          console.error('Error awaiting leadership:', error);
          setLeaderTabStatus(LeaderTabStatus.Error);
        }
      });

    return () => {
      isEffectCurrent = false;
      void leaderElection.die();
      void leaderChannel.close();
    };
  }, []);

  async function requestBrowserNotificationPermissionAndOptIn(): Promise<NotificationPermission> {
    if (!isBrowserNotificationSupported) {
      memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedOut);
      return 'default';
    }

    try {
      if (Notification.permission === 'granted') {
        memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedIn);
        setWatermarkSettings({ watermarkMilliseconds: Date.now() });
        return 'granted';
      }

      const nextPermission = await Notification.requestPermission();
      if (nextPermission === 'granted') {
        memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedIn);
        setWatermarkSettings({ watermarkMilliseconds: Date.now() });
      } else {
        memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedOut);
      }

      return nextPermission;
    } catch {
      memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedOut);
      return isBrowserNotificationSupported ? Notification.permission : 'default';
    }
  }

  return {
    isBrowserNotificationSupported,
    browserNotificationPermission,
    optInToNotificationsInAppStatus,
    isBrowserNotificationEnabled,
    leaderTabStatus,
    canRequestPermission,
    canEmitNotifications,
    watermarkMilliseconds: watermarkSettings.watermarkMilliseconds,
    memoizedSetWatermarkMilliseconds,
    requestBrowserNotificationPermissionAndOptIn,
    disableBrowserNotifications: () => memoizedSetOptInStatusAndDispatchEvent(OptInStatus.OptedOut),
  };
}

export const [BrowserNotificationsProvider, useBrowserNotificationsContext] =
  constate(useBrowserNotifications);
